/**
 * Achievement Unlock Modal Component
 * Displays a celebratory modal when achievements are unlocked
 * Features confetti animation and requires user interaction to dismiss
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Achievement, RARITY_COLORS } from '../data/achievements';

interface AchievementUnlockModalProps {
  achievement: Achievement | null;
  onClose: () => void;
}

const AchievementUnlockModal: React.FC<AchievementUnlockModalProps> = ({
  achievement,
  onClose,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (achievement) {
      // Trigger entrance animation after a brief delay
      const entranceTimer = setTimeout(() => {
        setIsVisible(true);
      }, 100);

      // Trigger confetti after entrance
      const confettiTimer = setTimeout(() => {
        setShowConfetti(true);
      }, 300);

      return () => {
        clearTimeout(entranceTimer);
        clearTimeout(confettiTimer);
      };
    }
  }, [achievement]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setShowConfetti(false);
    // Wait for exit animation before calling onClose
    setTimeout(onClose, 300);
  }, [onClose]);

  // Check if modals are disabled by user preference
  useEffect(() => {
    const modalsDisabled = localStorage.getItem('flashread_achievement_modals_disabled');
    if (modalsDisabled === 'true' && achievement) {
      // If modals are disabled, immediately call onClose without showing
      onClose();
    }
  }, [achievement, onClose]);

  if (!achievement) return null;

  const rarityColor = RARITY_COLORS[achievement.rarity];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={`
          absolute inset-0 bg-black/60 backdrop-blur-sm
          transition-opacity duration-300
          ${isVisible ? 'opacity-100' : 'opacity-0'}
        `}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`
          relative z-10 max-w-md w-full mx-4
          bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden
          border-4 ${rarityColor.split(' ')[1] || 'border-slate-300'}
          transform transition-all duration-300 ease-out
          ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-8'}
        `}
      >
        {/* Confetti Canvas */}
        {showConfetti && <ConfettiCanvas rarity={achievement.rarity} />}

        {/* Header with sparkle effect */}
        <div className={`
          px-6 py-4 bg-gradient-to-r ${getRarityGradient(achievement.rarity)}
          relative overflow-hidden
        `}>
          {/* Sparkle decorations */}
          <div className="absolute top-2 left-4 text-white/30 text-2xl animate-pulse">✨</div>
          <div className="absolute top-3 right-8 text-white/30 text-xl animate-pulse delay-75">⭐</div>
          <div className="absolute bottom-2 left-12 text-white/20 text-lg animate-pulse delay-150">💫</div>

          <h2 className="text-white font-bold text-xl text-center flex items-center justify-center gap-2">
            <span className="animate-bounce">🎉</span>
            Achievement Unlocked!
            <span className="animate-bounce delay-150">🎊</span>
          </h2>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Achievement Icon */}
          <div className={`
            mx-auto mb-6
            text-6xl p-6 rounded-full
            bg-slate-100 dark:bg-slate-700
            border-4 ${rarityColor.split(' ')[1] || 'border-slate-300'}
            shadow-lg animate-pulse
          `}>
            {achievement.icon}
          </div>

          {/* Achievement Name */}
          <h3 className={`
            text-2xl font-bold mb-2
            ${getRarityTextColor(achievement.rarity)}
          `}>
            {achievement.name}
          </h3>

          {/* Description */}
          <p className="text-slate-600 dark:text-slate-300 mb-6 text-lg">
            {achievement.description}
          </p>

          {/* XP Reward and Rarity Badge */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 px-4 py-2 rounded-full">
              <span className="text-amber-600 dark:text-amber-400 text-xl">⚡</span>
              <span className="text-amber-700 dark:text-amber-300 font-bold text-lg">
                +{achievement.xpReward} XP
              </span>
            </div>
            <div className={`
              px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide
              ${getRarityBgColor(achievement.rarity)}
            `}>
              {achievement.rarity}
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleClose}
            className="
              w-full py-3 px-6 rounded-xl font-bold text-lg
              bg-gradient-to-r from-blue-500 to-purple-600
              hover:from-blue-600 hover:to-purple-700
              text-white shadow-lg
              transform transition-all duration-200
              hover:scale-105 active:scale-95
              focus:outline-none focus:ring-4 focus:ring-blue-500/50
            "
          >
            🎉 Awesome!
          </button>

          {/* Optional: Settings link to disable modals */}
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            Tip: You can disable achievement popups in settings
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }

        @keyframes sparkle {
          0%, 100% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1); opacity: 1; }
        }

        .delay-75 { animation-delay: 75ms; }
        .delay-150 { animation-delay: 150ms; }
      `}</style>
    </div>
  );
};

// Confetti Canvas Component
const ConfettiCanvas: React.FC<{ rarity: Achievement['rarity'] }> = ({ rarity }) => {
  useEffect(() => {
    const canvas = document.getElementById('confetti-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Confetti colors based on rarity
    const colorMap: Record<string, string[]> = {
      common: ['#94a3b8', '#64748b', '#475569'],
      uncommon: ['#22c55e', '#4ade80', '#86efac'],
      rare: ['#3b82f6', '#60a5fa', '#93c5fd'],
      epic: ['#a855f7', '#c084fc', '#d8b4fe'],
      legendary: ['#f59e0b', '#fbbf24', '#fcd34d'],
      mythic: ['#ec4899', '#f472b6', '#f9a8d4'],
    };
    const colors = colorMap[rarity] || colorMap.common;

    // Create confetti particles
    const particles = Array.from({ length: 150 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 8 + 4,
      speedY: Math.random() * 3 + 2,
      speedX: Math.random() * 2 - 1,
      rotation: Math.random() * 360,
      rotationSpeed: Math.random() * 10 - 5,
    }));

    let animationId: number;
    let startTime = Date.now();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allOffScreen = true;
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;

      for (const p of particles) {
        // Update position
        p.y += p.speedY;
        p.x += p.speedX + Math.sin(elapsed + p.y / 100) * 0.5;
        p.rotation += p.rotationSpeed;

        // Draw particle
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size / 2);
        ctx.restore();

        // Check if still on screen
        if (p.y < canvas.height + 20) {
          allOffScreen = false;
        }
      }

      if (!allOffScreen) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [rarity]);

  return (
    <canvas
      id="confetti-canvas"
      className="fixed inset-0 pointer-events-none z-20"
      style={{ position: 'fixed', top: 0, left: 0 }}
    />
  );
};

// Helper functions
function getRarityGradient(rarity: Achievement['rarity']): string {
  switch (rarity) {
    case 'common':
      return 'from-slate-400 to-slate-600';
    case 'uncommon':
      return 'from-green-400 to-green-600';
    case 'rare':
      return 'from-blue-400 to-blue-600';
    case 'epic':
      return 'from-purple-400 to-purple-600';
    case 'legendary':
      return 'from-amber-400 to-orange-500';
    case 'mythic':
      return 'from-pink-400 to-rose-600';
    default:
      return 'from-slate-400 to-slate-600';
  }
}

function getRarityTextColor(rarity: Achievement['rarity']): string {
  switch (rarity) {
    case 'common':
      return 'text-slate-600 dark:text-slate-300';
    case 'uncommon':
      return 'text-green-600 dark:text-green-400';
    case 'rare':
      return 'text-blue-600 dark:text-blue-400';
    case 'epic':
      return 'text-purple-600 dark:text-purple-400';
    case 'legendary':
      return 'text-amber-600 dark:text-amber-400';
    case 'mythic':
      return 'text-pink-600 dark:text-pink-400';
    default:
      return 'text-slate-600 dark:text-slate-300';
  }
}

function getRarityBgColor(rarity: Achievement['rarity']): string {
  switch (rarity) {
    case 'common':
      return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    case 'uncommon':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'rare':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'epic':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'legendary':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'mythic':
      return 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400';
    default:
      return 'bg-slate-200 text-slate-700';
  }
}

export default AchievementUnlockModal;
